/**
 * Hero strip figures and the alert ring buffer.
 *
 * Emergencies are never dropped by age or by the ring cap. Cautions and
 * notices can be snoozed; a snooze is a timer, not a deletion. Acknowledge
 * hides an alert until its id changes. Expired snoozes are pruned on read
 * because the timer has fired — nothing else in storage is touched.
 */
import { coerceClockDate, formatUtcHm } from "./clock";
import { displayFlightNumber } from "./flightNumber";
import { ticketLifecycle } from "./opsBoard";
import { fuelKindForType, type FuelKind } from "./catalogs";
import {
  ofpTripFuelLb,
  presentFuelCompare,
  resolveMeasuredBurn,
  type LiveFuelStatus,
  type TankLoadKind,
} from "./fuelBurn";
import { convertFuel, formatFuelFigure, formatSignedFuel, fuelUsedToUsGal, type FuelUnit } from "./fuelUnits";
import { ofpPlannedFuelGal } from "./tripFuel";
import { CLASH_KIND_LABEL, detectClashes, type DispatchPlan } from "./dispatchBrain";
import { crewIdFromName, dutyViewsFor, emptyDutyRoster, type DutyRoster } from "./duty";
import type { GroundingHold } from "./flightGrounding";
import type { TripAssignment } from "./assignment";
import type {
  AirframeStatus,
  AircraftSpec,
  CharterRequest,
  CompanyProfile,
  CrewMember,
  MelItem,
  Mission,
  MissionOfpAttachment,
  SceneryAirport,
  Squawk,
  TicketLifecycle,
} from "./types";

export const HERO_ALERT_RING = 40;
export const CAUTION_PULSE_MS = 2 * 60_000;
export const HERO_ALERT_ACK_CAP = 400;

export const HERO_SNOOZE_MS = {
  "5m": 5 * 60_000,
  "15m": 15 * 60_000,
  "1h": 60 * 60_000,
  "4h": 4 * 60 * 60_000,
} as const;

export type HeroSnoozeDuration = keyof typeof HERO_SNOOZE_MS;

export const HERO_SNOOZE_OPTIONS: { id: HeroSnoozeDuration; label: string; ms: number }[] = [
  { id: "5m", label: "5 min", ms: HERO_SNOOZE_MS["5m"] },
  { id: "15m", label: "15 min", ms: HERO_SNOOZE_MS["15m"] },
  { id: "1h", label: "1 hour", ms: HERO_SNOOZE_MS["1h"] },
  { id: "4h", label: "4 hours", ms: HERO_SNOOZE_MS["4h"] },
];

export type HeroAlertBoardState = {
  snoozeUntil: Record<string, number>;
  ackedAt: Record<string, number>;
  seenAt: Record<string, number>;
};

export function emptyHeroAlertBoard(): HeroAlertBoardState {
  return { snoozeUntil: {}, ackedAt: {}, seenAt: {} };
}

function readNumMap(value: unknown): Record<string, number> {
  if (!value || typeof value !== "object") return {};
  const out: Record<string, number> = {};
  for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
    if (!key || typeof raw !== "number" || !Number.isFinite(raw)) continue;
    out[key] = raw;
  }
  return out;
}

function capMap(map: Record<string, number>, limit: number): Record<string, number> {
  const entries = Object.entries(map);
  if (entries.length <= limit) return map;
  entries.sort((a, b) => b[1] - a[1]);
  return Object.fromEntries(entries.slice(0, limit));
}

/** Drop expired snooze timers. Acks and first-seen times stay. */
export function pruneExpiredSnoozes(state: HeroAlertBoardState, now: number): HeroAlertBoardState {
  let changed = false;
  const snoozeUntil: Record<string, number> = {};
  for (const [id, until] of Object.entries(state.snoozeUntil)) {
    if (until > now) snoozeUntil[id] = until;
    else changed = true;
  }
  return changed ? { ...state, snoozeUntil } : state;
}

export function parseHeroAlertBoard(raw: string | null | undefined, now: number): HeroAlertBoardState {
  if (!raw) return emptyHeroAlertBoard();
  let data: unknown;
  try {
    data = JSON.parse(raw);
  } catch {
    return emptyHeroAlertBoard();
  }
  if (!data || typeof data !== "object") return emptyHeroAlertBoard();
  const rec = data as Record<string, unknown>;
  const state: HeroAlertBoardState = {
    snoozeUntil: readNumMap(rec.snoozeUntil),
    ackedAt: capMap(readNumMap(rec.ackedAt), HERO_ALERT_ACK_CAP),
    seenAt: capMap(readNumMap(rec.seenAt), HERO_ALERT_ACK_CAP),
  };
  return pruneExpiredSnoozes(state, now);
}

export function serializeHeroAlertBoard(state: HeroAlertBoardState): string {
  return JSON.stringify({
    v: 1,
    snoozeUntil: state.snoozeUntil,
    ackedAt: state.ackedAt,
    seenAt: state.seenAt,
  });
}

export function snoozeAlerts(
  state: HeroAlertBoardState,
  ids: readonly string[],
  duration: HeroSnoozeDuration,
  now: number,
): HeroAlertBoardState {
  const ms = HERO_SNOOZE_MS[duration];
  if (!ms || !ids.length) return state;
  const snoozeUntil = { ...state.snoozeUntil };
  let changed = false;
  for (const id of ids) {
    if (!id || state.ackedAt[id]) continue;
    snoozeUntil[id] = now + ms;
    changed = true;
  }
  return changed ? { ...state, snoozeUntil } : state;
}

export function unsnoozeAlerts(state: HeroAlertBoardState, ids: readonly string[]): HeroAlertBoardState {
  if (!ids.length) return state;
  const snoozeUntil = { ...state.snoozeUntil };
  let changed = false;
  for (const id of ids) {
    if (id in snoozeUntil) {
      delete snoozeUntil[id];
      changed = true;
    }
  }
  return changed ? { ...state, snoozeUntil } : state;
}

export function acknowledgeAlerts(
  state: HeroAlertBoardState,
  ids: readonly string[],
  now: number,
): HeroAlertBoardState {
  if (!ids.length) return state;
  const ackedAt = { ...state.ackedAt };
  const snoozeUntil = { ...state.snoozeUntil };
  let changed = false;
  for (const id of ids) {
    if (!id) continue;
    ackedAt[id] = now;
    if (id in snoozeUntil) delete snoozeUntil[id];
    changed = true;
  }
  if (!changed) return state;
  return { ...state, ackedAt: capMap(ackedAt, HERO_ALERT_ACK_CAP), snoozeUntil };
}

/** First time an id is shown. Repeat calls keep the original time. */
export function noteSeen(state: HeroAlertBoardState, ids: readonly string[], now: number): HeroAlertBoardState {
  let changed = false;
  const seenAt = { ...state.seenAt };
  for (const id of ids) {
    if (!id || seenAt[id]) continue;
    seenAt[id] = now;
    changed = true;
  }
  if (!changed) return state;
  return { ...state, seenAt: capMap(seenAt, HERO_ALERT_ACK_CAP) };
}

export function nextSnoozeWake(state: HeroAlertBoardState, now: number): number | null {
  let next: number | null = null;
  for (const until of Object.values(state.snoozeUntil)) {
    if (until <= now) return now;
    if (next == null || until < next) next = until;
  }
  return next;
}

export type HeroBoardTone = "info" | "warn" | "alert";

export type HeroBoardItem = {
  id: string;
  tone: HeroBoardTone;
};

export type HeroSnoozedItem<T> = T & { until: number };

export type HeroBoardPartition<T> = {
  active: T[];
  snoozed: HeroSnoozedItem<T>[];
  cautionCount: number;
  emergencyCount: number;
  noteCount: number;
};

/**
 * Split open alerts into active and snoozed. Acknowledged ids are omitted.
 * The ring keeps every emergency. Cautions and notices fill the remaining
 * room from the front of the list, so callers should pass newest first.
 */
export function partitionHeroAlerts<T extends HeroBoardItem>(
  alerts: readonly T[],
  state: HeroAlertBoardState,
  now: number,
  ring = HERO_ALERT_RING,
): HeroBoardPartition<T> {
  const active: T[] = [];
  const snoozed: HeroSnoozedItem<T>[] = [];
  const seen = new Set<string>();
  for (const alert of alerts) {
    if (!alert.id || seen.has(alert.id) || state.ackedAt[alert.id]) continue;
    seen.add(alert.id);
    const until = state.snoozeUntil[alert.id];
    if (typeof until === "number" && until > now) snoozed.push({ ...alert, until });
    else active.push(alert);
  }
  const emergencies = active.filter((alert) => alert.tone === "alert").length;
  const room = Math.max(0, ring - emergencies);
  let restLeft = room;
  const capped: T[] = [];
  for (const alert of active) {
    if (alert.tone === "alert") capped.push(alert);
    else if (restLeft > 0) {
      capped.push(alert);
      restLeft -= 1;
    }
  }
  return {
    active: capped,
    snoozed,
    cautionCount: capped.filter((alert) => alert.tone === "warn").length,
    emergencyCount: capped.filter((alert) => alert.tone === "alert").length,
    noteCount: capped.filter((alert) => alert.tone === "info").length,
  };
}

export function cautionStillPulsing(seenAt: number | undefined, now: number): boolean {
  if (seenAt == null) return true;
  return now - seenAt < CAUTION_PULSE_MS;
}

export function formatDepartureLead(remainingMs: number): string {
  if (!Number.isFinite(remainingMs)) return "—";
  const min = Math.round(remainingMs / 60_000);
  if (min > 0) {
    const h = Math.floor(min / 60);
    const m = min % 60;
    return h > 0 ? `${h}h ${String(m).padStart(2, "0")}m` : `${m}m`;
  }
  if (min === 0) return remainingMs < 0 ? "late" : "due";
  const late = Math.abs(min);
  const h = Math.floor(late / 60);
  const m = late % 60;
  return h > 0 ? `late ${h}h ${String(m).padStart(2, "0")}m` : `late ${late}m`;
}

export function formatEteMin(min: number | null | undefined): string {
  if (typeof min !== "number" || !Number.isFinite(min)) return "—";
  const rounded = Math.max(0, Math.round(min));
  const h = Math.floor(rounded / 60);
  const m = rounded % 60;
  return h > 0 ? `${h}h ${String(m).padStart(2, "0")}m` : `${m}m`;
}

export function formatSnoozeLeft(until: number, now: number): string {
  return formatDepartureLead(Math.max(0, until - now));
}

function zuluLabel(ms: number | null): string {
  if (ms == null || !Number.isFinite(ms)) return "—";
  return formatUtcHm(new Date(ms));
}

/** Same rule as arrival fuel: a positive finite US-gallon figure, otherwise none. */
function positiveGal(value: number | null | undefined): number | null {
  return typeof value === "number" && Number.isFinite(value) && value > 0 ? value : null;
}

function instantMs(value: string | null | undefined): number | null {
  if (!value) return null;
  const date = coerceClockDate(value);
  if (!date) return null;
  const ms = date.getTime();
  return Number.isFinite(ms) ? ms : null;
}

export type HeroFleetTailInput = {
  id: string;
  registration: string;
  status: AirframeStatus | string;
  name?: string;
};

export type HeroFleetMissionInput = {
  id: string;
  status: string;
  lifecycle?: string | null;
  activeBoundAt?: string | null;
  aircraftId?: string | null;
  registration?: string | null;
  departureIso?: string | null;
  eteMin?: number | null;
  flightNumber?: string | null;
  callsign?: string | null;
  originIcao?: string | null;
  destIcao?: string | null;
};

export type HeroFleetLive = {
  etaIso?: string | null;
  eteMin?: number | null;
};

export type HeroFleetRow = {
  id: string;
  registration: string;
  name: string;
  airborne: boolean;
  statusLabel: string;
  flight: string;
  route: string;
  eteLabel: string;
  etaLabel: string;
  missionId?: string;
};

export type HeroFleetStatus = {
  active: number;
  onGround: number;
  inAir: number;
  airborne: HeroFleetRow[];
  ground: HeroFleetRow[];
  inlineTails: string;
};

const GROUND_STATUS: Record<string, string> = {
  available: "On ground",
  ferry: "Ferry",
  grounded: "Grounded",
  in_mx: "Maintenance",
};

function missionLife(mission: HeroFleetMissionInput): TicketLifecycle {
  return ticketLifecycle({
    status: mission.status as "pending" | "in-progress" | "completed" | "cancelled",
    lifecycle: (mission.lifecycle ?? undefined) as TicketLifecycle | undefined,
    activeBoundAt: mission.activeBoundAt ?? undefined,
  });
}

function flightLabel(mission: { flightNumber?: string | null; callsign?: string | null }): string {
  return displayFlightNumber({
    flightNumber: mission.flightNumber ?? undefined,
    callsign: mission.callsign ?? undefined,
  });
}

function routeLabel(mission: { originIcao?: string | null; destIcao?: string | null }): string {
  const origin = mission.originIcao?.trim();
  const dest = mission.destIcao?.trim();
  if (origin && dest) return `${origin}→${dest}`;
  return origin || dest || "—";
}

function plannedEtaMs(mission: HeroFleetMissionInput): number | null {
  const std = instantMs(mission.departureIso);
  const ete = typeof mission.eteMin === "number" && mission.eteMin > 0 ? mission.eteMin : null;
  if (std == null || ete == null) return null;
  return std + ete * 60_000;
}

export function buildHeroFleetStatus(input: {
  now: Date;
  fleet: readonly HeroFleetTailInput[];
  missions: readonly HeroFleetMissionInput[];
  live?: Readonly<Record<string, HeroFleetLive | undefined>>;
}): HeroFleetStatus {
  const nowMs = input.now.getTime();
  const fleetById = new Map(input.fleet.map((tail) => [tail.id, tail]));
  const airborneMissionByTail = new Map<string, HeroFleetMissionInput>();
  let active = 0;
  for (const mission of input.missions) {
    const open = mission.status === "pending" || mission.status === "in-progress";
    if (open) active += 1;
    const life = missionLife(mission);
    if (life !== "airborne" && life !== "diverting") continue;
    const tailId = mission.aircraftId?.trim() || mission.registration?.trim() || mission.id;
    if (!airborneMissionByTail.has(tailId)) airborneMissionByTail.set(tailId, mission);
  }

  const airborne: HeroFleetRow[] = [];
  for (const [tailId, mission] of airborneMissionByTail) {
    const tail = fleetById.get(tailId) ?? fleetById.get(mission.aircraftId ?? "");
    const live = input.live?.[mission.id];
    const etaMs = instantMs(live?.etaIso) ?? plannedEtaMs(mission);
    const remaining =
      etaMs != null ? Math.max(0, (etaMs - nowMs) / 60_000) : typeof live?.eteMin === "number" ? live.eteMin : mission.eteMin;
    airborne.push({
      id: tail?.id || tailId,
      registration: tail?.registration || mission.registration?.trim() || tailId,
      name: tail?.name || "",
      airborne: true,
      statusLabel: missionLife(mission) === "diverting" ? "Diverting" : "In the air",
      flight: flightLabel(mission),
      route: routeLabel(mission),
      eteLabel: formatEteMin(typeof remaining === "number" ? remaining : null),
      etaLabel: zuluLabel(etaMs),
      missionId: mission.id,
    });
  }
  airborne.sort((a, b) => a.registration.localeCompare(b.registration));

  const airborneIds = new Set(airborne.map((row) => row.id));
  const ground: HeroFleetRow[] = input.fleet
    .filter((tail) => !airborneIds.has(tail.id))
    .map((tail) => ({
      id: tail.id,
      registration: tail.registration,
      name: tail.name || "",
      airborne: false,
      statusLabel: GROUND_STATUS[tail.status] ?? "On ground",
      flight: "",
      route: "",
      eteLabel: "—",
      etaLabel: "—",
    }))
    .sort((a, b) => a.registration.localeCompare(b.registration));

  return {
    active,
    onGround: ground.length,
    inAir: airborne.length,
    airborne,
    ground,
    inlineTails: airborne.map((row) => row.registration).filter(Boolean).join(" "),
  };
}

export type HeroNextDeparture = {
  missionId: string;
  flight: string;
  route: string;
  registration: string;
  stdLabel: string;
  lead: string;
  remainingMs: number;
};

export function nextHeroDeparture(input: {
  now: Date;
  missions: readonly HeroFleetMissionInput[];
}): HeroNextDeparture | null {
  const nowMs = input.now.getTime();
  const candidates: { mission: HeroFleetMissionInput; std: number }[] = [];
  for (const mission of input.missions) {
    if (mission.status !== "pending" && mission.status !== "in-progress") continue;
    const life = missionLife(mission);
    if (life === "airborne" || life === "diverting" || life === "arrived" || life === "diverted" || life === "completed" || life === "cancelled") {
      continue;
    }
    const std = instantMs(mission.departureIso);
    if (std == null) continue;
    candidates.push({ mission, std });
  }
  if (!candidates.length) return null;
  const future = candidates.filter((row) => row.std >= nowMs).sort((a, b) => a.std - b.std);
  const picked = future[0] ?? candidates.slice().sort((a, b) => b.std - a.std)[0]!;
  const remainingMs = picked.std - nowMs;
  return {
    missionId: picked.mission.id,
    flight: flightLabel(picked.mission),
    route: routeLabel(picked.mission),
    registration: picked.mission.registration?.trim() || "",
    stdLabel: zuluLabel(picked.std),
    lead: formatDepartureLead(remainingMs),
    remainingMs,
  };
}

export type HeroFuelVariance = {
  missionId: string;
  flight: string;
  route: string;
  actualGal: number;
  plannedGal: number;
  deltaGal: number;
  pct: number;
  unit?: FuelUnit;
  status?: LiveFuelStatus;
  chip?: string;
  tooltip?: string;
  heading?: string;
  tripText?: string;
  actualText?: string;
  deltaText?: string;
};

export function latestPostFlightFuelVariance(input: {
  missions: readonly {
    id: string;
    ofpAttachment?: Pick<MissionOfpAttachment, "plannedTripGal" | "plannedFuelGal" | "fuelPlan" | "aircraftType" | "plannedTripLbs"> | null;
    flightNumber?: string | null;
    callsign?: string | null;
    originIcao?: string | null;
    destIcao?: string | null;
  }[];
  logs: readonly {
    tripId?: string | null;
    arrivedAtUtc?: string | null;
    updatedAtUtc?: string | null;
    arrival?: { fuelUsedGal?: number | null; reportedAtUtc?: string | null } | null;
  }[];
  fuelUnitFor?: (missionId: string) => FuelUnit | undefined;
  fuelKindFor?: (missionId: string) => FuelKind | undefined;
}): HeroFuelVariance | null {
  const missions = new Map(input.missions.map((mission) => [mission.id, mission]));
  let best: { stamp: number; row: HeroFuelVariance } | null = null;
  for (const log of input.logs) {
    const mission = log.tripId ? missions.get(log.tripId) : undefined;
    if (!mission) continue;
    const actual = positiveGal(log.arrival?.fuelUsedGal);
    if (actual == null) continue;
    const planned = ofpPlannedFuelGal(mission.ofpAttachment);
    const stamp = instantMs(log.arrival?.reportedAtUtc) ?? instantMs(log.arrivedAtUtc) ?? instantMs(log.updatedAtUtc) ?? 0;
    const unit = input.fuelUnitFor?.(mission.id) ?? "lb";
    const kind = input.fuelKindFor?.(mission.id) ?? fuelKindForType(mission.ofpAttachment?.aircraftType);
    const actualText = formatFuelFigure(convertFuel(actual, "gal", unit, kind), unit);
    if (planned == null || planned <= 0) {
      const row: HeroFuelVariance = {
        missionId: mission.id,
        flight: flightLabel(mission),
        route: routeLabel(mission),
        actualGal: actual,
        plannedGal: 0,
        deltaGal: 0,
        pct: 0,
        unit,
        status: "no_trip_fuel",
        chip: "No trip fuel",
        heading: "Post-flight fuel",
        tripText: "No trip fuel",
        actualText,
        deltaText: "—",
        tooltip:
          "No SimBrief trip fuel on this leg. Block and ramp fuel are not used as the burn. The used figure is the tail's fuel unit.",
      };
      if (!best || stamp >= best.stamp) best = { stamp, row };
      continue;
    }
    const deltaGal = actual - planned;
    const pct = ((actual - planned) / planned) * 100;
    const tripText = formatFuelFigure(convertFuel(planned, "gal", unit, kind), unit);
    const signed = formatSignedFuel(convertFuel(deltaGal, "gal", unit, kind), unit);
    const row: HeroFuelVariance = {
      missionId: mission.id,
      flight: flightLabel(mission),
      route: routeLabel(mission),
      actualGal: actual,
      plannedGal: planned,
      deltaGal,
      pct,
      unit,
      status: "compared",
      chip: `Burn vs plan ${signed}`,
      heading: "Burn vs plan",
      tripText,
      actualText,
      deltaText: signed,
      tooltip: `Post-flight fuel variance ${signed}. Used ${actualText}. SimBrief trip fuel ${tripText}. Block fuel is not the burn compare.`,
    };
    if (!best || stamp >= best.stamp) best = { stamp, row };
  }
  return best?.row ?? null;
}

/** Hero chip when no flight is measuring burn. Not a leftover post-flight delta. */
export function idleFuelHero(): HeroFuelVariance {
  return {
    missionId: "",
    flight: "",
    route: "",
    actualGal: 0,
    plannedGal: 0,
    deltaGal: 0,
    pct: 0,
    unit: "lb",
    status: "waiting",
    chip: "—",
    heading: "Burn vs plan",
    tripText: "—",
    actualText: "—",
    deltaText: "—",
    tooltip: "No active flight. Burn vs plan appears while a trip is live.",
  };
}

export function liveFuelHeroVariance(input: {
  active: boolean;
  missionId?: string;
  flight?: string;
  route?: string;
  ofp?: Pick<MissionOfpAttachment, "plannedTripGal" | "plannedFuelGal" | "fuelPlan" | "aircraftType" | "plannedTripLbs"> | null;
  icaoType?: string;
  unit: FuelUnit;
  fuel?: FuelKind;
  tankLb: number | null;
  flowLb: number | null;
  flowSeen: boolean;
  unlimitedFuel: boolean;
  fuelDump: boolean;
  tankLoad?: TankLoadKind | null;
}): HeroFuelVariance | null {
  if (!input.active) return null;
  const measured = resolveMeasuredBurn({
    tankLb: input.tankLb,
    flowLb: input.flowLb,
    flowSeen: input.flowSeen,
    unlimitedFuel: input.unlimitedFuel,
    fuelDump: input.fuelDump,
  });
  const view = presentFuelCompare({
    tripLb: ofpTripFuelLb(input.ofp, input.icaoType),
    measured,
    unit: input.unit,
    fuel: input.fuel ?? fuelKindForType(input.icaoType),
    tankLoad: input.tankLoad,
    heading: "Burn vs plan",
  });
  const plannedGal = ofpPlannedFuelGal(input.ofp) ?? 0;
  const actualGal =
    measured.reliable && measured.burnLb != null
      ? fuelUsedToUsGal(measured.burnLb, "lb", input.fuel ?? fuelKindForType(input.icaoType))
      : 0;
  const deltaGal = plannedGal > 0 && measured.reliable && measured.burnLb != null ? actualGal - plannedGal : 0;
  const pct = plannedGal > 0 && measured.reliable && measured.burnLb != null ? (deltaGal / plannedGal) * 100 : 0;
  return {
    missionId: input.missionId ?? "",
    flight: input.flight ?? "",
    route: input.route ?? "",
    actualGal,
    plannedGal,
    deltaGal,
    pct,
    unit: view.unit,
    status: view.status,
    chip: view.chip,
    tooltip: view.tooltip,
    heading: view.heading,
    tripText: view.tripLabel,
    actualText: view.actualLabel,
    deltaText: view.deltaLabel,
  };
}

export type HeroStripAirborne = {
  registration: string;
  flight: string;
  route: string;
  eteLabel: string;
  etaLabel: string;
};

export type HeroStripGround = {
  registration: string;
  statusLabel: string;
};

export type HeroStripAlert = {
  id: string;
  tone: HeroBoardTone;
  source: string;
  text: string;
  detail?: string;
};

export type HeroStripDeparture = {
  missionId: string;
  flight: string;
  route: string;
  registration: string;
  stdIso: string;
  stdLabel: string;
};

/** Company hero the companion reads. Snooze and acknowledge stay on the desk. */
export type HeroStripPublish = {
  v: 1;
  atUtc: string;
  fleet: {
    active: number;
    onGround: number;
    inAir: number;
    airborne: HeroStripAirborne[];
    ground: HeroStripGround[];
  };
  departure: HeroStripDeparture | null;
  alerts: HeroStripAlert[];
  cautionCount: number;
  emergencyCount: number;
};

const HOLD_KIND_LABEL: Record<GroundingHold["kind"], string> = {
  hotel: "Crew hotel",
  parking: "Parking",
  complete: "Completion",
};

function missionInputs(missions: readonly Mission[]): HeroFleetMissionInput[] {
  return missions.map((mission) => ({
    id: mission.id,
    status: mission.status,
    lifecycle: mission.lifecycle,
    activeBoundAt: mission.activeBoundAt,
    aircraftId: mission.aircraftId,
    registration: mission.registration,
    departureIso: mission.departureIso,
    eteMin: mission.eteMin,
    flightNumber: mission.flightNumber,
    callsign: mission.callsign,
    originIcao: mission.originIcao,
    destIcao: mission.destIcao,
  }));
}

function clip(value: string | undefined, cap = 240): string | undefined {
  const text = value?.trim();
  if (!text) return undefined;
  return text.length > cap ? text.slice(0, cap) : text;
}

/**
 * Counts, Zulu ETE/ETA, alert tiers, and the next departure.
 * Session notices stay on the desk. A bad clash pass does not drop the rest.
 */
export function publishHeroStrip(input: {
  now?: Date;
  missions?: readonly Mission[];
  fleet?: readonly AircraftSpec[];
  crew?: readonly CrewMember[];
  duty?: DutyRoster | null;
  assignments?: readonly TripAssignment[];
  scenery?: readonly SceneryAirport[];
  company?: Pick<CompanyProfile, "enforceDutyClock" | "homeIcao" | "mxBases"> | null;
  profileName?: string | null;
  dispatchPlan?: DispatchPlan | null;
  groundingHolds?: readonly GroundingHold[];
  squawks?: readonly Squawk[];
  melItems?: readonly MelItem[];
  inbox?: readonly CharterRequest[];
}): HeroStripPublish {
  const now = input.now ?? new Date();
  const missions = input.missions ?? [];
  const fleet = input.fleet ?? [];
  const asMissions = missionInputs(missions);
  const fleetStatus = buildHeroFleetStatus({
    now,
    fleet: fleet.map((tail) => ({
      id: tail.id,
      registration: tail.registration,
      status: tail.status,
      name: tail.name,
    })),
    missions: asMissions,
  });
  const next = nextHeroDeparture({ now, missions: asMissions });
  const stdIso = next
    ? missions.find((mission) => mission.id === next.missionId)?.departureIso?.trim() || ""
    : "";
  const alerts: HeroStripAlert[] = [];
  if (input.company?.enforceDutyClock !== false && input.duty) {
    const named = (input.profileName ?? "").trim();
    const dutyCrew = [
      ...(named ? [{ id: crewIdFromName(named), name: named }] : []),
      ...(input.crew ?? []).map((member) => ({ id: member.id, name: member.displayName || member.id })),
    ];
    for (const view of dutyViewsFor(input.duty, dutyCrew, now)) {
      if (view.level === "ok") continue;
      alerts.push({
        id: `duty-${view.crewId}-${view.level}`,
        tone: view.level === "alert" ? "alert" : "warn",
        source: "Duty",
        text:
          view.level === "alert"
            ? `${view.crewName} is at the duty limit`
            : `${view.crewName} is nearing the duty limit`,
        detail: `${view.statusLabel}. ${view.certificateLabel}`,
      });
    }
  }
  for (const tail of fleet) {
    if (tail.status !== "grounded" && tail.status !== "in_mx") continue;
    alerts.push({
      id: `mx-${tail.id}-${tail.status}`,
      tone: tail.status === "grounded" ? "alert" : "warn",
      source: tail.status === "grounded" ? "Grounded" : "Maintenance",
      text: `${tail.registration} ${tail.status === "grounded" ? "is grounded" : "is in maintenance"}`,
      detail: tail.name,
    });
  }
  try {
    const clashes = detectClashes({
      missions: [...missions],
      assignments: [...(input.assignments ?? [])],
      fleet: [...fleet],
      crew: [...(input.crew ?? [])],
      duty: input.duty ?? emptyDutyRoster(),
      scenery: [...(input.scenery ?? [])],
      inbox: [...(input.inbox ?? [])],
      squawks: [...(input.squawks ?? [])],
      melItems: [...(input.melItems ?? [])],
      now,
      homeIcao: input.company?.homeIcao,
      mxBases: input.company?.mxBases,
    });
    for (const clash of clashes) {
      alerts.push({
        id: `clash-${clash.id}`,
        tone: clash.severity === "red" ? "alert" : "warn",
        source: "Dispatch assist",
        text: `${CLASH_KIND_LABEL[clash.kind]} · ${clash.message}`,
      });
    }
  } catch {
    /* A partial board still publishes fleet and departure. */
  }
  for (const hold of input.groundingHolds ?? []) {
    if (!hold?.missionId || !hold.kind) continue;
    alerts.push({
      id: `gh-${hold.missionId}-${hold.kind}-${hold.atUtc ?? ""}`,
      tone: "warn",
      source: "Grounding hold",
      text: `${hold.voided ? "Removed" : "Held"} ${HOLD_KIND_LABEL[hold.kind] ?? hold.kind} ${hold.icao} · ${hold.flightNumber}`,
      detail: hold.reason,
    });
  }
  if (input.dispatchPlan?.status === "proposed") {
    const plan = input.dispatchPlan;
    alerts.push({
      id: `plan-${plan.id}`,
      tone: "info",
      source: "Dispatch plan",
      text: `Proposed plan · ${plan.actions.length} step${plan.actions.length === 1 ? "" : "s"} · ${plan.createdByName}`,
      detail: plan.summary,
    });
  }
  const partitioned = partitionHeroAlerts(alerts, emptyHeroAlertBoard(), now.getTime());
  return {
    v: 1,
    atUtc: now.toISOString(),
    fleet: {
      active: fleetStatus.active,
      onGround: fleetStatus.onGround,
      inAir: fleetStatus.inAir,
      airborne: fleetStatus.airborne.map((row) => ({
        registration: row.registration,
        flight: row.flight,
        route: row.route,
        eteLabel: row.eteLabel,
        etaLabel: row.etaLabel,
      })),
      ground: fleetStatus.ground.map((row) => ({
        registration: row.registration,
        statusLabel: row.statusLabel,
      })),
    },
    departure:
      next && stdIso
        ? {
            missionId: next.missionId,
            flight: next.flight,
            route: next.route,
            registration: next.registration,
            stdIso,
            stdLabel: next.stdLabel,
          }
        : null,
    alerts: partitioned.active.map((alert) => ({
      id: alert.id,
      tone: alert.tone,
      source: alert.source,
      text: alert.text,
      ...(clip(alert.detail) ? { detail: clip(alert.detail) } : {}),
    })),
    cautionCount: partitioned.cautionCount,
    emergencyCount: partitioned.emergencyCount,
  };
}

function hydrateAlert(raw: unknown): HeroStripAlert | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  const id = typeof row.id === "string" ? row.id.trim() : "";
  const text = typeof row.text === "string" ? row.text.trim() : "";
  const tone = row.tone === "alert" || row.tone === "warn" || row.tone === "info" ? row.tone : null;
  if (!id || !text || !tone) return null;
  const detail = typeof row.detail === "string" ? row.detail.trim() : "";
  return {
    id,
    tone,
    source: typeof row.source === "string" && row.source.trim() ? row.source.trim() : "Desk",
    text,
    ...(detail ? { detail } : {}),
  };
}

/** Missing or broken strips hydrate to null. Older snapshots keep loading. */
export function hydrateHeroStrip(raw: unknown): HeroStripPublish | null {
  if (!raw || typeof raw !== "object") return null;
  const row = raw as Record<string, unknown>;
  if (row.v !== 1) return null;
  const fleet = row.fleet && typeof row.fleet === "object" ? (row.fleet as Record<string, unknown>) : null;
  if (!fleet) return null;
  const num = (value: unknown) => (typeof value === "number" && Number.isFinite(value) ? value : 0);
  const airborne = Array.isArray(fleet.airborne)
    ? fleet.airborne.flatMap((item) => {
        if (!item || typeof item !== "object") return [];
        const tail = item as Record<string, unknown>;
        const registration = typeof tail.registration === "string" ? tail.registration : "";
        if (!registration) return [];
        return [
          {
            registration,
            flight: typeof tail.flight === "string" ? tail.flight : "",
            route: typeof tail.route === "string" ? tail.route : "",
            eteLabel: typeof tail.eteLabel === "string" ? tail.eteLabel : "—",
            etaLabel: typeof tail.etaLabel === "string" ? tail.etaLabel : "—",
          },
        ];
      })
    : [];
  const ground = Array.isArray(fleet.ground)
    ? fleet.ground.flatMap((item) => {
        if (!item || typeof item !== "object") return [];
        const tail = item as Record<string, unknown>;
        const registration = typeof tail.registration === "string" ? tail.registration : "";
        if (!registration) return [];
        return [{ registration, statusLabel: typeof tail.statusLabel === "string" ? tail.statusLabel : "On ground" }];
      })
    : [];
  const departureRaw = row.departure;
  let departure: HeroStripDeparture | null = null;
  if (departureRaw && typeof departureRaw === "object") {
    const dep = departureRaw as Record<string, unknown>;
    const stdIso = typeof dep.stdIso === "string" ? dep.stdIso.trim() : "";
    const missionId = typeof dep.missionId === "string" ? dep.missionId : "";
    if (stdIso && missionId) {
      departure = {
        missionId,
        flight: typeof dep.flight === "string" ? dep.flight : "",
        route: typeof dep.route === "string" ? dep.route : "",
        registration: typeof dep.registration === "string" ? dep.registration : "",
        stdIso,
        stdLabel: typeof dep.stdLabel === "string" ? dep.stdLabel : "",
      };
    }
  }
  const alerts = Array.isArray(row.alerts) ? row.alerts.flatMap((item) => hydrateAlert(item) ?? []) : [];
  return {
    v: 1,
    atUtc: typeof row.atUtc === "string" ? row.atUtc : "",
    fleet: {
      active: num(fleet.active),
      onGround: num(fleet.onGround),
      inAir: num(fleet.inAir),
      airborne,
      ground,
    },
    departure,
    alerts,
    cautionCount: num(row.cautionCount),
    emergencyCount: num(row.emergencyCount),
  };
}

